import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle, Trash2, Plus, Paperclip, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, TopNotice, makeUploader, ResubmitFromSection,
} from "@/components/FormShell";

const EMPTY_GUEST = { sl_no: 1, name: "", age: "", relationship: "", idProof: null };

export default function NewGuest() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);
  const [attachments, setAttachments] = useState([]); // for id proofs
  const [form, setForm] = useState({
    employeeName: user?.name || "",
    designation: "",
    mainDept: "",
    subDept: "",
    flatNo: "",
    maritalStatus: "Single",
    mobileNo: "",
    guests: [{ ...EMPTY_GUEST }],
    vehicleType: "Not Applicable",
    vehicleRegNo: "",
    daysCount: "",
    fromDate: "",
    toDate: "",
    undertakingAgreed: false,
    is_emergency: false,
    emergency_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGuest = (i, k, v) => setForm((f) => ({
    ...f,
    guests: f.guests.map((g, j) => j === i ? { ...g, [k]: v } : g),
  }));

  const addGuest = () => setForm((f) => f.guests.length >= 10 ? f : ({
    ...f, guests: [...f.guests, { ...EMPTY_GUEST, sl_no: f.guests.length + 1 }],
  }));
  const removeGuest = (i) => setForm((f) => f.guests.length <= 1 ? f : ({
    ...f, guests: f.guests.filter((_, j) => j !== i)
                       .map((g, j) => ({ ...g, sl_no: j + 1 })),
  }));

  const uploadIdProof = async (i, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File larger than 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const proof = { field_name: `guest_id_proof_${i + 1}`,
                      filename: data.filename, url: data.url, size: data.size };
      setGuest(i, "idProof", proof);
      setAttachments((a) => {
        const filtered = a.filter((x) => x.field_name !== proof.field_name);
        return [...filtered, proof];
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  // late-submission indicator (client-side hint only; backend recomputes)
  const dayDiff = useMemo(() => {
    if (!form.fromDate) return null;
    const diffMs = new Date(form.fromDate) - new Date();
    return Math.floor(diffMs / 86400000);
  }, [form.fromDate]);
  const isLate = dayDiff !== null && dayDiff < 1;

  const computedDays = useMemo(() => {
    if (!form.fromDate || !form.toDate) return null;
    const d = Math.floor((new Date(form.toDate) - new Date(form.fromDate)) / 86400000) + 1;
    return d > 0 ? d : null;
  }, [form.fromDate, form.toDate]);
  const daysMismatch = computedDays && form.daysCount &&
                        Number(form.daysCount) !== computedDays;

  const validate = () => {
    const req = ["employeeName", "designation", "mainDept", "subDept", "flatNo",
                 "maritalStatus", "mobileNo", "vehicleType", "daysCount",
                 "fromDate", "toDate"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) { toast.error(`Please fill: ${k}`); return false; }
    }
    if (!/^\d{10}$/.test(String(form.mobileNo).trim())) {
      toast.error("Mobile number must be 10 digits"); return false;
    }
    if (["Two-Wheeler", "Four-Wheeler"].includes(form.vehicleType) &&
        !String(form.vehicleRegNo || "").trim()) {
      toast.error("Vehicle registration number is required"); return false;
    }
    if (new Date(form.toDate) < new Date(form.fromDate)) {
      toast.error("'To' date must be on or after 'From' date"); return false;
    }
    if (!form.undertakingAgreed) {
      toast.error("You must agree to the undertaking"); return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    for (const [i, g] of form.guests.entries()) {
      if (!g.name?.trim() || !g.age?.toString().trim() || !g.relationship?.trim()) {
        toast.error(`Please complete guest #${i + 1}`); return false;
      }
      if (!g.idProof) {
        toast.error(`ID proof required for guest #${i + 1}`); return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { form_type: "guest", parent_ref_code: parentRefCode || undefined, form_data: { ...form, attachments } };
      const { data } = await api.post("/requisitions/guest", payload);
      toast.success(`Submitted · ${data.ref_code}`, { duration: 5000 });
      nav(`/requisitions/${data.ref_code}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto relative">
        <FormLogo />
        <TopNotice>
          Employee must submit this form prior to the arrival of Guest(s) minimum of one day
          in advance. Post-facto application will not be entertained unless on an emergency.
          The responsibility of the guest(s) lies solely with the host.
        </TopNotice>

        {isLate && (
          <div className="mt-3 text-sm bg-[#fcebe9] text-[#b3261e] border border-[#b3261e]/40 rounded px-3 py-2 flex items-start gap-2"
               data-testid="guest-late-banner">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>FYI: Form submitted beyond ideal timeline — <strong>Last-minute requirement.</strong></div>
          </div>
        )}

        <div className="mt-4 mb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#8a3f19] font-semibold">Guest Visit - Stay in Staff Quarters</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight"
              data-testid="guest-form-heading">
            Application for hosting overnight guest(s)
          </h1>
        </div>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setField("emergency_reason", v)}
        />

        <ResubmitFromSection
          isResubmit={isResubmit}
          onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
          parentRefCode={parentRefCode}
          onSelectHistory={(fd, ref) => {
            setForm((f) => ({ ...f, ...fd }));
            setParentRefCode(ref);
          }}
        />

        {/* Section I */}
        <Section numeral="I" title="Employee / Applicant details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name of Employee" required>
              <input className="input-vgws" value={form.employeeName}
                     onChange={(e) => setField("employeeName", e.target.value)}
                     data-testid="guest-employee-name" />
            </Field>
            <Field label="Designation" required>
              <input className="input-vgws" value={form.designation}
                     onChange={(e) => setField("designation", e.target.value)}
                     data-testid="guest-designation" />
            </Field>
            <Field label="Main Department" required>
              <select className="input-vgws" value={form.mainDept}
                      onChange={(e) => { setField("mainDept", e.target.value); setField("subDept", ""); }}
                      data-testid="guest-main-dept">
                <option value="">— Select —</option>
                {depts.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Sub-department" required>
              <select className="input-vgws" value={form.subDept}
                      onChange={(e) => setField("subDept", e.target.value)}
                      disabled={!form.mainDept}
                      data-testid="guest-sub-dept">
                <option value="">— Select —</option>
                {subDepts.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Flat No." required>
              <input className="input-vgws" value={form.flatNo}
                     onChange={(e) => setField("flatNo", e.target.value)}
                     data-testid="guest-flat-no" />
            </Field>
            <Field label="Mobile No. (10 digits)" required>
              <input className="input-vgws" value={form.mobileNo}
                     onChange={(e) => setField("mobileNo", e.target.value.replace(/\D/g, "").slice(0, 10))}
                     data-testid="guest-mobile-no" />
            </Field>
            <div className="sm:col-span-2">
              <span className="block text-sm font-medium text-[#1f2320] mb-1.5">Marital status <span className="text-[#b3261e]">*</span></span>
              <RadioGroup name="marital-status" value={form.maritalStatus}
                          onChange={(v) => setField("maritalStatus", v)}
                          options={["Married", "Single"]}
                          testid="guest-marital" />
            </div>
          </div>
        </Section>

        {/* Section II — Guests */}
        <Section numeral="II" title="Particulars of the Guest(s)">
          <div className="overflow-x-auto" data-testid="guest-guests-table">
            <table className="w-full border border-[#e2d7c3] rounded">
              <thead className="bg-[#faf5eb] text-xs text-[#4a524c] uppercase tracking-widest">
                <tr>
                  <th className="p-2 text-left w-12">SL.</th>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left w-20">Age</th>
                  <th className="p-2 text-left">Relationship</th>
                  <th className="p-2 text-left w-72">ID Proof (PDF/JPG/PNG, 10MB)</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.guests.map((g, i) => (
                  <tr key={i} className="border-t border-[#e2d7c3]"
                      data-testid={`guest-row-${i}`}>
                    <td className="p-2">{i + 1}</td>
                    <td className="p-2">
                      <input className="input-vgws" value={g.name}
                             onChange={(e) => setGuest(i, "name", e.target.value)}
                             data-testid={`guest-name-${i}`} />
                    </td>
                    <td className="p-2">
                      <input className="input-vgws" value={g.age}
                             onChange={(e) => setGuest(i, "age", e.target.value.replace(/\D/g, ""))}
                             data-testid={`guest-age-${i}`} />
                    </td>
                    <td className="p-2">
                      <input className="input-vgws" value={g.relationship}
                             onChange={(e) => setGuest(i, "relationship", e.target.value)}
                             data-testid={`guest-relation-${i}`} />
                    </td>
                    <td className="p-2">
                      <label className="btn-ghost cursor-pointer text-xs" data-testid={`guest-idproof-label-${i}`}>
                        <Paperclip size={12} /> {g.idProof ? "Replace" : "Upload"}
                        <input type="file" accept="application/pdf,image/*" className="hidden"
                               onChange={(e) => uploadIdProof(i, e.target.files?.[0])}
                               data-testid={`guest-idproof-${i}`} />
                      </label>
                      {g.idProof && (
                        <div className="text-xs text-[#2d6b6b] mt-1 truncate max-w-[16rem]"
                             title={g.idProof.filename} data-testid={`guest-idproof-name-${i}`}>
                          ✔ {g.idProof.filename}
                        </div>
                      )}
                    </td>
                    <td className="p-2 text-center">
                      <button type="button"
                              className="p-1 rounded hover:bg-[#fcebe9] text-[#b3261e] disabled:opacity-30"
                              onClick={() => removeGuest(i)}
                              disabled={form.guests.length <= 1}
                              data-testid={`guest-remove-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-ghost mt-2"
                  onClick={addGuest}
                  disabled={form.guests.length >= 10}
                  data-testid="guest-add-row">
            <Plus size={14} /> Add guest
          </button>
          {uploading && (
            <span className="ml-2 text-xs text-[#6e7770] inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Uploading…
            </span>
          )}
          <p className="text-[11px] text-[#6e7770] mt-1">Min 1 · Max 10 guests. ID proof mandatory per guest.</p>
        </Section>

        {/* Section III — Vehicle */}
        <Section numeral="III" title="Guest(s) Vehicle details">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Vehicle Type" required>
              <select className="input-vgws" value={form.vehicleType}
                      onChange={(e) => setField("vehicleType", e.target.value)}
                      data-testid="guest-vehicle-type">
                <option>Not Applicable</option>
                <option>Two-Wheeler</option>
                <option>Four-Wheeler</option>
              </select>
            </Field>
            {["Two-Wheeler", "Four-Wheeler"].includes(form.vehicleType) && (
              <Field label="Vehicle Registration No." required>
                <input className="input-vgws" value={form.vehicleRegNo}
                       onChange={(e) => setField("vehicleRegNo", e.target.value.toUpperCase())}
                       data-testid="guest-vehicle-regno" />
              </Field>
            )}
          </div>
        </Section>

        {/* Section IV — Duration */}
        <Section numeral="IV" title="Duration of Stay">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Permission required for (day(s))" required>
              <input type="number" className="input-vgws" value={form.daysCount}
                     min={1}
                     onChange={(e) => setField("daysCount", e.target.value)}
                     data-testid="guest-days-count" />
            </Field>
            <Field label="From date" required>
              <input type="date" className="input-vgws" value={form.fromDate}
                     onChange={(e) => setField("fromDate", e.target.value)}
                     data-testid="guest-from-date" />
            </Field>
            <Field label="To date" required>
              <input type="date" className="input-vgws" value={form.toDate}
                     min={form.fromDate || undefined}
                     onChange={(e) => setField("toDate", e.target.value)}
                     data-testid="guest-to-date" />
            </Field>
          </div>
          {daysMismatch && (
            <div className="mt-2 text-xs text-[#b48a45] bg-[#f9f2e0] border border-[#b48a45]/40 rounded px-3 py-2"
                 data-testid="guest-days-mismatch">
              Note: You have entered <strong>{form.daysCount}</strong> day(s), but From-To spans
              <strong> {computedDays}</strong> day(s). Please recheck.
            </div>
          )}
        </Section>

        {/* Section V — Undertaking */}
        <Section numeral="V" title="Undertaking">
          <p className="text-sm text-[#1f2320] bg-[#faf5eb] border-l-4 border-[#7a1f2b] px-3 py-2 rounded mb-3">
            I undertake that I shall be fully responsible for the conduct of the above-mentioned
            guest(s) on the school campus.
          </p>
          <label className="inline-flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-1"
                   checked={form.undertakingAgreed}
                   onChange={(e) => setField("undertakingAgreed", e.target.checked)}
                   data-testid="guest-undertaking-agreed" />
            <span>I agree to the above undertaking <span className="text-[#b3261e]">*</span></span>
          </label>
        </Section>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button className="btn-vgws bg-[#8a3f19] hover:bg-[#6a2f13] border-[#6a2f13]"
                  onClick={submit}
                  disabled={submitting || uploading}
                  data-testid="guest-submit">
            <Send size={16} /> {submitting ? "Submitting…" : "Submit for approval"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
